import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, BadgeCheck, Smartphone, MapPin, Repeat, Target,
  Percent, TrendingUp, Layers, Users, FlaskConical, Sparkles, TrendingDown,
  MousePointerClick, Download, UserCheck, LineChart,
} from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';

const GREEN = '#4fae3f';
const CREATIVE_IMG = 'https://images.unsplash.com/photo-1642052502780-8ee67e3bf930?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const kpis = [
  { icon: MousePointerClick, value: '87.3M+', label: 'Clicks', raw: '87,289,293' },
  { icon: Download, value: '227K+', label: 'Installs', raw: '227,066' },
  { icon: UserCheck, value: '77.4K+', label: 'Registrations', raw: '77,479' },
  { icon: Repeat, value: '5,097', label: 'Recurring Investments', raw: '5,097' },
];

const overview = [
  { icon: Smartphone, label: 'Platform', value: 'iOS' },
  { icon: MapPin, label: 'Geo', value: 'United States' },
  { icon: Repeat, label: 'Model', value: 'Pay-per-FRIRT' },
  { icon: Target, label: 'Objective', value: 'First-Time Recurring Investments' },
];

const goals = [
  { icon: TrendingDown, title: 'Efficient CAC', body: 'Drive quality installs while holding customer acquisition cost below $70.' },
  { icon: Percent, title: 'Install → Registration', body: 'Achieve a 13–15% install-to-registration-completion rate.' },
  { icon: Repeat, title: 'Convert to FRIRT', body: 'Turn registered users into First-Time Recurring Investors.' },
  { icon: TrendingUp, title: 'Activity & Retention', body: 'Increase in-app investment activity and long-term retention.' },
];

const strategy = [
  { icon: Layers, title: 'Cross-Channel Media Buying', body: 'Coordinated acquisition across In-App, DSP and programmatic inventory to reach high-intent iOS audiences at scale.', tags: ['In-App', 'DSP', 'Programmatic'] },
  { icon: Users, title: 'Advanced Audience Segmentation', body: 'Precision targeting focused on financially motivated, high-intent users most likely to invest recurrently.', tags: ['High-intent', 'Financially motivated'] },
  { icon: FlaskConical, title: 'A/B Testing', body: 'Systematic testing of App Store screenshots, creative copy and on-page conversion-rate optimization.', tags: ['Store screenshots', 'Copy', 'CRO'] },
  { icon: Sparkles, title: 'Creative Automation at Scale', body: 'A creative engine producing hundreds of variants with continuous, real-time performance optimization.', highlight: '550+', highlightLabel: 'creative variants' },
];

const efficiency = [
  { title: 'Customer Acquisition Cost', value: '< $70', note: 'CAC target', type: 'cap' },
  { title: 'Install → Registration Completion', value: '13–15%', note: 'benchmark range', min: 13, max: 15, scaleMax: 30 },
  { title: 'Registration Completion → FRIRT', value: '72–75%', note: 'benchmark range', min: 72, max: 75, scaleMax: 100 },
];

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">{children}</span>
);

function RangeBar({ min, max, scaleMax }) {
  const left = (min / scaleMax) * 100;
  const width = ((max - min) / scaleMax) * 100;
  return (
    <div className="mt-4 h-2.5 w-full rounded-full bg-slate-100 relative overflow-hidden">
      <div
        className="absolute top-0 bottom-0 rounded-full"
        style={{ left: `${left}%`, width: `${Math.max(width, 4)}%`, background: 'linear-gradient(90deg,#2563eb,#1d4ed8)' }}
      />
    </div>
  );
}

export default function AcornsCaseStudy() {
  useReveal('acorns');
  useSeo({
    title: 'Acorns Case Study | Interscope Ads',
    description: 'Acorns case study: how Interscope Ads drove First-Time Recurring Investments (FRIRT) with performance marketing and fintech app user acquisition in the US.',
    keywords: 'Acorns case study, performance marketing, app user acquisition, fintech user acquisition, FRIRT, mobile app acquisition, US app marketing',
  });

  return (
    <main className="pt-32" data-testid="acorns-case-study">
      {/* 1. HERO */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563eb] transition-colors mb-8">
          <ArrowLeft size={16} /> All case studies
        </Link>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="reveal">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-14 h-14 rounded-2xl bg-white border border-slate-200 grid place-items-center p-2 shadow-sm">
                <img src="/acorns-logo.png" alt="Acorns" className="max-w-full max-h-full object-contain" />
              </span>
              <div>
                <span className="inline-block rounded-full bg-[#2563eb]/10 px-3 py-1 text-xs font-semibold text-[#2563eb] border border-[#2563eb]/20">Fintech • User Acquisition</span>
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><BadgeCheck size={13} /> Adjust verified</span>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-500 mb-2">Acorns — Invest For Your Future</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
              Driving <span className="text-gradient">First-Time Recurring Investments</span> at Scale
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">
              Interscope Ads helped Acorns acquire and convert high-intent iOS users across the United States through a performance-driven acquisition program built around recurring investment behavior.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Badge>iOS</Badge>
              <Badge>United States</Badge>
              <Badge>Pay-per-FRIRT</Badge>
            </div>
          </div>

          {/* Supporting dashboard-style visual */}
          <div className="reveal">
            <div className="rounded-3xl bg-gradient-to-br from-[#0f172a] to-[#0b132b] p-6 md:p-8 shadow-2xl shadow-slate-900/20" data-testid="acorns-hero-dashboard">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-300 text-sm font-medium"><LineChart size={16} className="text-blue-400" /> Campaign Snapshot</div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><BadgeCheck size={12} /> Adjust</span>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                {kpis.map((k) => (
                  <div key={k.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <k.icon size={18} className="text-blue-400" />
                    <div className="font-display text-2xl font-bold text-white mt-2 tabular-nums">{k.value}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{k.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 text-[11px] text-slate-500">Adjust campaign data · United States · Sep–Dec 2025</div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CAMPAIGN OVERVIEW */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 mt-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {overview.map((o) => (
            <div key={o.label} className="reveal glass glass-hover rounded-2xl p-6">
              <span className="w-10 h-10 rounded-xl bg-[#2563eb]/10 grid place-items-center text-[#2563eb] mb-4"><o.icon size={20} /></span>
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">{o.label}</div>
              <div className="mt-1 font-display text-lg font-bold text-slate-900">{o.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. CHALLENGE / GOALS */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <div className="reveal max-w-2xl mb-12">
          <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">The Challenge</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Ambitious goals for a high-intent investor base</h2>
          <p className="mt-4 text-slate-600">Acorns needed efficient, high-quality acquisition that didn't just deliver installs — it had to convert into genuine recurring investment behavior.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {goals.map((g) => (
            <div key={g.title} className="reveal glass glass-hover rounded-2xl p-6">
              <span className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center mb-4" style={{ color: GREEN }}><g.icon size={22} /></span>
              <h3 className="font-display text-base font-bold text-slate-900">{g.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. STRATEGY & EXECUTION */}
      <section className="relative bg-slate-50/70 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 section-pad">
          <div className="reveal max-w-2xl mb-12">
            <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Strategy &amp; Execution</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">A full-funnel acquisition engine</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {strategy.map((s) => (
              <div key={s.title} className="reveal glass glass-hover rounded-2xl p-7 flex gap-5">
                <span className="w-12 h-12 rounded-xl bg-[#2563eb]/10 grid place-items-center text-[#2563eb] shrink-0"><s.icon size={24} /></span>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                  {s.tags && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.tags.map((t) => (
                        <span key={t} className="rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">{t}</span>
                      ))}
                    </div>
                  )}
                  {s.highlight && (
                    <div className="mt-4 inline-flex items-baseline gap-2 rounded-xl bg-emerald-50 px-4 py-2">
                      <span className="font-display text-2xl font-bold" style={{ color: GREEN }}>{s.highlight}</span>
                      <span className="text-xs font-semibold text-emerald-700">{s.highlightLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PERFORMANCE DASHBOARD */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Campaign Performance</span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">The numbers that moved</h2>
          <p className="mt-3 text-sm font-medium text-slate-500">Adjust campaign data · United States · Sep–Dec 2025</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="acorns-kpi-grid">
          {kpis.map((k) => (
            <div key={k.label} className="reveal glass glass-hover rounded-2xl p-7 text-center">
              <span className="w-12 h-12 rounded-xl bg-[#2563eb]/10 grid place-items-center text-[#2563eb] mx-auto mb-4"><k.icon size={22} /></span>
              <div className="font-display text-3xl md:text-4xl font-bold text-gradient tabular-nums">{k.value}</div>
              <div className="mt-1 text-sm font-medium text-slate-600">{k.label}</div>
              <div className="mt-2 text-[11px] text-slate-400 tabular-nums">{k.raw}</div>
            </div>
          ))}
        </div>
        <p className="reveal mt-8 text-center text-xs text-slate-400 max-w-2xl mx-auto">
          Dashboard totals are reported directly from Adjust for the campaign period and are shown independently of the campaign benchmark ranges below.
        </p>
      </section>

      {/* 6. CONVERSION EFFICIENCY */}
      <section className="relative bg-slate-50/70 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 section-pad">
          <div className="reveal max-w-2xl mb-12">
            <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Conversion Efficiency</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Campaign benchmarks</h2>
            <p className="mt-4 text-slate-600">Efficiency targets and conversion ranges achieved across the program — reported separately from the raw Adjust totals.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-3" data-testid="acorns-efficiency">
            {efficiency.map((e) => (
              <div key={e.title} className="reveal glass rounded-2xl p-7">
                <div className="text-sm font-medium text-slate-600">{e.title}</div>
                <div className="mt-2 font-display text-4xl font-bold text-slate-900">{e.value}</div>
                {e.type === 'cap' ? (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    <TrendingDown size={14} /> Held below target
                  </div>
                ) : (
                  <RangeBar min={e.min} max={e.max} scaleMax={e.scaleMax} />
                )}
                <div className="mt-3 text-[11px] uppercase tracking-wider font-semibold text-slate-400">{e.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CREATIVE / CAMPAIGN VISUAL */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Creative at Scale</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Built to convert calm, confident investors</h2>
            <p className="mt-4 text-slate-600">A high-velocity creative program produced <span className="font-semibold text-slate-900">550+ variants</span>, continuously optimized in real time across App Store assets, copy and placements to find the messages that turned installs into recurring investors.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Badge>Store screenshots</Badge>
              <Badge>Creative copy</Badge>
              <Badge>Real-time optimization</Badge>
            </div>
          </div>
          <div className="reveal">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
              <img src={CREATIVE_IMG} alt="Acorns investing campaign creative" className="w-full h-[360px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b12]/80 via-[#0a0b12]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-white grid place-items-center p-1.5 shrink-0">
                  <img src="/acorns-logo.png" alt="Acorns" className="max-w-full max-h-full object-contain" />
                </span>
                <div>
                  <div className="text-white font-display font-bold leading-tight">Acorns</div>
                  <div className="text-slate-300 text-xs">Invest For Your Future</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. RESULTS / IMPACT */}
      <section className="relative bg-slate-50/70 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-5 md:px-8 section-pad text-center">
          <span className="reveal inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">The Impact</span>
          <h2 className="reveal font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">Building a high-value user base</h2>
          <p className="reveal mt-6 text-lg text-slate-600">Delivered a high-value user base with strong recurring investment behavior.</p>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="reveal relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f172a] to-[#0b132b] p-10 md:p-16 text-center">
          <div className="absolute inset-0" aria-hidden>
            <div className="absolute w-[360px] h-[360px] rounded-full bg-[#2563eb]/25 blur-[100px] -top-24 -left-10" />
            <div className="absolute w-[320px] h-[320px] rounded-full bg-[#4f46e5]/20 blur-[100px] -bottom-24 -right-10" />
          </div>
          <div className="relative">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white max-w-2xl mx-auto leading-tight">
              Want to scale your next acquisition campaign?
            </h2>
            <div className="mt-8">
              <Link data-testid="acorns-cta-button" to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 font-semibold text-white hover:bg-blue-500 transition-colors shadow-lg shadow-blue-600/30">
                Talk to Interscope Ads <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
